<template>
  <main class="my-pip">
    <vue-element-loading
      :active="loading"
      :is-full-screen="false"
      background-color="#f5f8f6"
      color="#14663a"
      spinner="bar-fade-scale"
    />

    <section class="pip-hero">
      <div class="hero-copy">
        <span class="eyebrow">Performance management</span>
        <h1>My PIP</h1>
        <p>Track the activities, outputs and outcomes assigned to you.</p>
      </div>
      <div class="hero-meta">
        <span>Financial year</span>
        <strong>{{ financialYear || '—' }}</strong>
      </div>
    </section>

    <div v-if="error" class="message error-message">
      <v-icon color="#b42318" size="20">mdi-alert-circle-outline</v-icon>
      <div><strong>Could not load your PIP</strong><span>{{ error }}</span></div>
      <button type="button" @click="load">Try again</button>
    </div>

    <template v-else-if="!loading">
      <section v-if="records.length" class="summary-grid">
        <article class="summary-card delivery">
          <span>Due delivery</span>
          <strong>{{ summary.due ? summary.dueDeliveryRate + '%' : '—' }}</strong>
          <small>{{ number(summary.delivered) }} delivered of {{ number(summary.due) }} due assessments</small>
        </article>
        <article class="summary-card">
          <span>Review items</span>
          <strong>{{ number(summary.reviewItems) }}</strong>
          <small>{{ number(summary.total) }} quarterly assessments</small>
        </article>
        <article class="summary-card good">
          <span>Met</span>
          <strong>{{ number(summary.met) }}</strong>
          <small>{{ number(summary.delayedMet) }} completed after deadline</small>
        </article>
        <article class="summary-card attention">
          <span>Needs attention</span>
          <strong>{{ needsAttentionCount }}</strong>
          <small>{{ number(summary.notMet) }} not met · {{ number(summary.overdue) }} overdue</small>
        </article>
        <article class="summary-card pending">
          <span>Upcoming</span>
          <strong>{{ number(summary.pending) }}</strong>
          <small>Pending work that is not overdue</small>
        </article>
      </section>

      <section v-if="records.length" class="work-panel">
        <header class="panel-head">
          <div>
            <span class="eyebrow dark">Your assigned evidence</span>
            <h2>PIP activities and results</h2>
            <p>Status and evidence are refreshed from the division PIP sheets.</p>
          </div>
          <button class="refresh-button" type="button" @click="load">
            <v-icon size="17">mdi-refresh</v-icon> Refresh
          </button>
        </header>

        <div class="filters">
          <label class="search-box">
            <v-icon size="19" color="#74837b">mdi-magnify</v-icon>
            <input v-model.trim="search" type="search" placeholder="Search project, activity or ID" />
          </label>
          <div class="filter-tabs" role="group" aria-label="Filter PIP items">
            <button
              v-for="option in filterOptions"
              :key="option.value"
              type="button"
              :class="{ active: filter === option.value }"
              @click="filter = option.value"
            >
              {{ option.label }} <b>{{ option.count }}</b>
            </button>
          </div>
        </div>

        <div v-if="filteredGroups.length" class="project-list">
          <article v-for="group in filteredGroups" :key="group.key" class="project-card">
            <header class="project-head">
              <div class="project-icon"><v-icon color="#14663a">mdi-folder-outline</v-icon></div>
              <div>
                <h3>{{ group.name }}</h3>
                <p v-if="group.code">{{ group.code }}<template v-if="group.division"> · {{ group.division }}</template></p>
                <p v-else-if="group.linked">{{ group.division || 'Linked WTI project' }}</p>
                <p v-else>PIP project name</p>
              </div>
              <span>{{ group.rows.length }} assessment{{ group.rows.length === 1 ? '' : 's' }}</span>
            </header>

            <div class="item-list">
              <section v-for="record in group.rows" :key="record._id" class="pip-item">
                <div class="item-primary">
                  <div class="item-labels">
                    <span class="type-pill">{{ typeLabel(record.itemType) }}</span>
                    <strong>{{ record.itemId || 'ID not provided' }}</strong>
                    <span v-if="record.quarter" class="quarter-pill">{{ quarterLabel(record.quarter) }}</span>
                  </div>
                  <h4>{{ record.description || 'Review item not provided' }}</h4>
                  <p v-if="record.remarks" class="remarks"><b>Remarks / evidence:</b> {{ record.remarks }}</p>
                </div>

                <div class="item-status">
                  <span :class="['status-pill', category(record)]">{{ statusLabel(record) }}</span>
                  <div class="date-grid">
                    <span><small>Start date</small><b>{{ formatDate(record.startDate) }}</b></span>
                    <span><small>Deadline</small><b>{{ formatDate(record.endDate) }}</b></span>
                  </div>
                  <p :class="['deadline-note', deadlineClass(record)]">{{ deadlineLabel(record) }}</p>
                </div>
              </section>
            </div>
          </article>
        </div>

        <div v-else class="empty filtered-empty">
          <v-icon size="38" color="#8b9991">mdi-filter-off-outline</v-icon>
          <h3>No items match this filter</h3>
          <p>Clear the search or select another status.</p>
        </div>
      </section>

      <section v-else class="empty empty-state">
        <div class="empty-icon"><v-icon size="48" color="#4f8065">mdi-clipboard-check-outline</v-icon></div>
        <h2>No PIP items are assigned to you</h2>
        <p>When a visible activity, output or outcome is mapped to your employee record, it will appear here automatically.</p>
        <button type="button" @click="load"><v-icon size="17">mdi-refresh</v-icon> Check again</button>
      </section>
    </template>
  </main>
</template>

<script>
import axios from "axios";

const DAY_MS = 24 * 60 * 60 * 1000;

export default {
  name: "MyPip",
  data() {
    return {
      loading: true,
      error: "",
      financialYear: "",
      summary: {},
      records: [],
      search: "",
      filter: "all",
    };
  },
  computed: {
    needsAttentionCount() {
      return Number(this.summary.notMet || 0) + Number(this.summary.overdue || 0);
    },
    filterOptions() {
      return [
        { value: "all", label: "All", count: this.records.length },
        { value: "attention", label: "Needs attention", count: this.records.filter(row => ["notMet", "overdue"].includes(this.category(row))).length },
        { value: "pending", label: "Upcoming", count: this.records.filter(row => this.category(row) === "pending").length },
        { value: "completed", label: "Completed", count: this.records.filter(row => ["met", "delayedMet"].includes(this.category(row))).length },
      ];
    },
    filteredRecords() {
      const needle = this.search.toLowerCase();
      return this.records.filter((record) => {
        const category = this.category(record);
        if (this.filter === "attention" && !["notMet", "overdue"].includes(category)) return false;
        if (this.filter === "pending" && category !== "pending") return false;
        if (this.filter === "completed" && !["met", "delayedMet"].includes(category)) return false;
        if (!needle) return true;
        const project = record.linkedProject || {};
        return [project.name, project.code, project.divisionName, record.sheetName, record.itemId, record.description, record.remarks, record.quarter]
          .some(value => String(value || "").toLowerCase().includes(needle));
      });
    },
    filteredGroups() {
      const groups = new Map();
      this.filteredRecords.forEach((record) => {
        const linked = record.linkedProject;
        const key = linked && linked.id ? String(linked.id) : `sheet:${record.sheetName || "unnamed"}`;
        if (!groups.has(key)) {
          groups.set(key, {
            key,
            name: linked && linked.name ? linked.name : (record.sheetName || "Unnamed PIP project"),
            code: linked && linked.code,
            division: linked && linked.divisionName,
            linked: Boolean(linked),
            rows: [],
          });
        }
        groups.get(key).rows.push(record);
      });
      return Array.from(groups.values());
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const response = await axios.get("/pip/me", {
          headers: { "x-auth-token": localStorage.getItem("token") },
        });
        if (!response.data || !response.data.status) throw new Error((response.data && response.data.msg) || "PIP data is unavailable");
        this.financialYear = response.data.financialYear || "";
        this.summary = response.data.summary || {};
        this.records = Array.isArray(response.data.records) ? response.data.records : [];
      } catch (error) {
        this.error = (error.response && error.response.data && error.response.data.msg) || error.message || "Please try again.";
      } finally {
        this.loading = false;
      }
    },
    number(value) {
      return Number(value || 0).toLocaleString("en-IN");
    },
    normalizedStatus(record) {
      return String((record && record.status) || "").trim().toLowerCase();
    },
    todayUtc() {
      const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Kolkata",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).formatToParts(new Date());
      const values = Object.fromEntries(parts.filter(part => part.type !== "literal").map(part => [part.type, Number(part.value)]));
      return Date.UTC(values.year, values.month - 1, values.day);
    },
    dateUtc(value) {
      if (!value) return null;
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return null;
      return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
    },
    category(record) {
      const status = this.normalizedStatus(record);
      if (status === "met") return "met";
      if (status.includes("delayed") && status.includes("met")) return "delayedMet";
      if (status === "not met" || status === "notmet") return "notMet";
      if (["n/a", "na", "not applicable"].includes(status)) return "notApplicable";
      const deadline = this.dateUtc(record && record.endDate);
      if (deadline !== null && deadline < this.todayUtc()) return "overdue";
      return "pending";
    },
    statusLabel(record) {
      return {
        met: "Met",
        delayedMet: "Delayed met",
        notMet: "Not met",
        overdue: "Overdue",
        notApplicable: "Not applicable",
        pending: "Pending",
      }[this.category(record)];
    },
    typeLabel(type) {
      return { activity: "Activity", output: "Output", outcome: "Outcome" }[type] || "Review item";
    },
    quarterLabel(value) {
      const label = String(value || "").trim().toUpperCase();
      return label.startsWith("Q") ? label : `Q${label}`;
    },
    formatDate(value) {
      if (!value) return "Not provided";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "Not provided";
      return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
    },
    dayDifference(record) {
      const deadline = this.dateUtc(record && record.endDate);
      if (deadline === null) return null;
      return Math.round((deadline - this.todayUtc()) / DAY_MS);
    },
    deadlineLabel(record) {
      const days = this.dayDifference(record);
      if (days === null) return "Deadline not provided";
      if (days < 0) {
        const elapsed = Math.abs(days);
        if (["met", "delayedMet"].includes(this.category(record))) return `Deadline passed ${elapsed} day${elapsed === 1 ? "" : "s"} ago`;
        return `Overdue by ${elapsed} day${elapsed === 1 ? "" : "s"}`;
      }
      if (days === 0) return "Due today";
      return `Due in ${days} day${days === 1 ? "" : "s"}`;
    },
    deadlineClass(record) {
      const days = this.dayDifference(record);
      if (days === null) return "neutral";
      if (days < 0) return "late";
      if (days <= 7) return "soon";
      return "future";
    },
  },
};
</script>

<style scoped>
.my-pip{min-height:calc(100vh - 130px);background:#f5f8f6;color:#1d3026;font-family:Arial,sans-serif;padding:4px}.pip-hero{display:flex;align-items:center;justify-content:space-between;gap:24px;padding:27px 30px;margin-bottom:18px;border-radius:17px;background:linear-gradient(135deg,#0e5032,#19764a);color:#fff;box-shadow:0 13px 32px rgba(14,80,50,.16)}.eyebrow{display:block;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:1.2px;color:#b8ddc8}.eyebrow.dark{color:#527264}.hero-copy h1{font-size:28px;line-height:1.15;margin:5px 0 6px}.hero-copy p{margin:0;color:#d7eade;font-size:13px}.hero-meta{text-align:right;min-width:130px}.hero-meta span,.hero-meta strong{display:block}.hero-meta span{font-size:10px;text-transform:uppercase;letter-spacing:.7px;color:#c5e1d1}.hero-meta strong{font-size:20px;margin-top:5px}.summary-grid{display:grid;grid-template-columns:repeat(5,minmax(145px,1fr));gap:12px;margin-bottom:18px}.summary-card{min-height:115px;display:flex;flex-direction:column;justify-content:center;padding:16px 18px;border:1px solid #dfe8e3;border-top:4px solid #789587;border-radius:13px;background:#fff;box-shadow:0 4px 16px rgba(31,67,49,.04)}.summary-card.delivery{border-top-color:#14663a;background:linear-gradient(145deg,#fff,#f0f7f3)}.summary-card.good{border-top-color:#2e7d32}.summary-card.attention{border-top-color:#c33c32}.summary-card.pending{border-top-color:#718899}.summary-card>span{color:#6e7e75;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.5px}.summary-card>strong{font-size:29px;line-height:1.1;margin:6px 0 4px;color:#203c2d}.summary-card.attention>strong{color:#b42318}.summary-card small{color:#849189;font-size:10.5px;line-height:1.35}.work-panel{border:1px solid #dfe8e3;border-radius:15px;background:#fff;overflow:hidden}.panel-head{display:flex;justify-content:space-between;align-items:center;gap:20px;padding:20px 22px;border-bottom:1px solid #e5ece8}.panel-head h2{font-size:19px;margin:4px 0}.panel-head p{margin:0;color:#7b8981;font-size:11.5px}.refresh-button,.empty-state button{display:inline-flex;align-items:center;gap:6px;padding:9px 13px;border:1px solid #cfe0d6;border-radius:9px;background:#fff;color:#155f3a;font-size:11px;font-weight:800}.filters{display:flex;align-items:center;justify-content:space-between;gap:14px;padding:14px 20px;background:#f8faf9;border-bottom:1px solid #e7eeea}.search-box{width:min(350px,100%);height:41px;display:flex;align-items:center;gap:8px;padding:0 12px;border:1px solid #d8e3dd;border-radius:9px;background:#fff}.search-box input{min-width:0;flex:1;border:0;outline:0;color:#243c2f;font-size:12px}.filter-tabs{display:flex;gap:6px;flex-wrap:wrap}.filter-tabs button{padding:8px 10px;border:1px solid #d8e3dd;border-radius:9px;background:#fff;color:#68786f;font-size:10.5px;font-weight:800}.filter-tabs button.active{border-color:#14663a;background:#e7f2eb;color:#14663a}.filter-tabs b{display:inline-block;min-width:18px;margin-left:3px;padding:1px 5px;border-radius:9px;background:#edf2ef}.project-list{padding:16px;display:grid;gap:14px}.project-card{border:1px solid #dfe8e3;border-radius:13px;overflow:hidden}.project-head{display:flex;align-items:center;gap:11px;padding:13px 15px;background:#f5f9f7;border-bottom:1px solid #e2eae5}.project-icon{width:37px;height:37px;display:flex;align-items:center;justify-content:center;border-radius:9px;background:#e4f1e9}.project-head>div:nth-child(2){min-width:0;flex:1}.project-head h3{font-size:14px;margin:0 0 3px}.project-head p{margin:0;color:#7c8982;font-size:10.5px}.project-head>span{padding:5px 8px;border-radius:9px;background:#e5eee9;color:#496456;font-size:9.5px;font-weight:800;white-space:nowrap}.pip-item{display:grid;grid-template-columns:minmax(0,1fr) 290px;gap:24px;padding:17px 18px;border-top:1px solid #e9efec}.pip-item:first-child{border-top:0}.item-labels{display:flex;align-items:center;gap:8px;flex-wrap:wrap}.type-pill,.quarter-pill{padding:4px 7px;border-radius:7px;background:#eaf1ed;color:#476154;font-size:9px;font-weight:800;text-transform:uppercase}.quarter-pill{background:#edf1f6;color:#596a7a}.item-labels>strong{font-size:11px;color:#345143}.item-primary h4{font-size:13px;line-height:1.5;margin:9px 0 0;color:#21352a}.remarks{margin:10px 0 0;padding:9px 11px;border-radius:8px;background:#f8faf9;color:#637269;font-size:10.5px;line-height:1.45;white-space:pre-wrap}.item-status{border-left:1px solid #e5ebe8;padding-left:19px}.status-pill{display:inline-block;padding:5px 9px;border-radius:999px;background:#edf1ef;color:#65736b;font-size:10px;font-weight:800}.status-pill.met{background:#e5f4e9;color:#267137}.status-pill.delayedMet{background:#fff3d8;color:#946200}.status-pill.notMet,.status-pill.overdue{background:#fde8e6;color:#b42318}.date-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:13px}.date-grid span{display:flex;flex-direction:column;gap:3px}.date-grid small{color:#88948d;font-size:8.5px;text-transform:uppercase;font-weight:800}.date-grid b{font-size:10.5px;color:#43594d}.deadline-note{margin:10px 0 0;font-size:10px;font-weight:800}.deadline-note.late{color:#b42318}.deadline-note.soon{color:#9a6504}.deadline-note.future{color:#4b7460}.deadline-note.neutral{color:#829087}.empty{text-align:center}.filtered-empty{padding:50px 20px}.filtered-empty h3{margin:10px 0 4px}.filtered-empty p{margin:0;color:#7d8a83;font-size:11px}.empty-state{padding:70px 24px;border:1px solid #dfe8e3;border-radius:15px;background:#fff}.empty-icon{width:82px;height:82px;display:flex;align-items:center;justify-content:center;margin:0 auto 17px;border-radius:50%;background:#e9f3ed}.empty-state h2{font-size:20px;margin:0 0 7px}.empty-state p{max-width:520px;margin:0 auto 18px;color:#728078;font-size:12px;line-height:1.5}.empty-state button{margin:auto}.message{display:flex;align-items:center;gap:11px;padding:15px;border-radius:11px}.message div{display:flex;flex-direction:column;gap:3px;flex:1}.message span{font-size:11px}.message button{border:0;background:#b42318;color:#fff;padding:8px 11px;border-radius:8px;font-weight:800}.error-message{background:#fdecea;color:#8e281f}@media(max-width:1050px){.summary-grid{grid-template-columns:repeat(3,1fr)}.pip-item{grid-template-columns:1fr}.item-status{border-left:0;border-top:1px solid #e6ece8;padding:14px 0 0}}@media(max-width:760px){.pip-hero,.panel-head,.filters{align-items:flex-start;flex-direction:column}.hero-meta{text-align:left}.summary-grid{grid-template-columns:repeat(2,1fr)}.filters,.search-box{width:100%}.filter-tabs{width:100%}.pip-item{padding:15px}.project-head{align-items:flex-start}.project-head>span{display:none}}@media(max-width:460px){.summary-grid{grid-template-columns:1fr}.date-grid{grid-template-columns:1fr}.pip-hero{padding:22px}.hero-copy h1{font-size:24px}}
</style>
